import { TeamPerson } from '@numeo/types-main-web-app'
import { useEffect, useMemo } from 'react'
import { shallowEqual } from 'react-redux'
import { useAppDispatch, useAppSelector } from '@/hooks'
import type { RootState } from '@/store'
import { teamApi } from '@/store/api/teamApi'
import { personFromUser } from '@/utils/team/teamRows'
import { selectTeamApplicationId, useTeamDirectoryEnabled } from './useTeamDirectoryEnabled'

interface PeopleState {
    /** The people found, by id. An id nobody answers to is absent. */
    people: ReadonlyMap<string, TeamPerson>
    /** Some of the ids are still being asked for. */
    isLoading: boolean
    /** A request for some of the ids failed: those ids are absent though somebody may answer to them. */
    isError: boolean
}

const EMPTY: ReadonlyMap<string, TeamPerson> = new Map()

/**
 * Names (and role, avatar, removed) for the ids a row points at: an actor, an assignee, a truck's drivers and
 * dispatchers. Each id is cached on its own for five minutes and every id asked in the same tick goes in one
 * `GET /team/people/v1` request; the directory lists fill the same cache. Not narrowed by Groups. Before the directory
 * serves the company, names come from `application.users`.
 */
export const usePeople = (ids: readonly (string | null | undefined)[]): PeopleState => {
    const dispatch = useAppDispatch()
    const enabled = useTeamDirectoryEnabled()
    const applicationId = useAppSelector(selectTeamApplicationId)
    const key = [...new Set(ids.filter((id): id is string => !!id).map(String))].sort().join(',')
    const wanted = useMemo(() => (key ? key.split(',') : []), [key])

    useEffect(() => {
        if (!enabled || !applicationId || !wanted.length) return
        const subscriptions = wanted.map((id) => dispatch(teamApi.endpoints.getPerson.initiate({ applicationId, id })))
        return () => subscriptions.forEach((subscription) => subscription.unsubscribe())
    }, [dispatch, enabled, applicationId, wanted])

    // One memoized selector per id, made once per id list rather than on every store update.
    const selectors = useMemo(
        () => (enabled && applicationId ? wanted.map((id) => teamApi.endpoints.getPerson.select({ applicationId, id })) : []),
        [enabled, applicationId, wanted]
    )
    const found = useAppSelector((state: RootState) => (enabled ? selectors.map((select) => select(state).data) : undefined), shallowEqual)
    const failed = useAppSelector((state: RootState) => (enabled ? selectors.map((select) => select(state).isError) : undefined), shallowEqual)
    const users = useAppSelector((state) => (enabled ? undefined : state.auth.application?.users))

    return useMemo((): PeopleState => {
        if (!enabled) {
            if (!wanted.length || !users) return { people: EMPTY, isLoading: false, isError: false }
            const set = new Set(wanted)
            const people = new Map<string, TeamPerson>()
            for (const user of users) {
                if (!set.has(String(user.id))) continue
                const person = personFromUser(user)
                if (person) people.set(person.id, person)
            }
            return { people, isLoading: false, isError: false }
        }
        const people = new Map<string, TeamPerson>()
        for (const person of found ?? []) if (person) people.set(person.id, person)
        // A failed id is settled, not loading: a screen waiting on it must not wait forever.
        return {
            people,
            isLoading: (found ?? []).some((person, index) => person === undefined && !failed?.[index]),
            isError: (failed ?? []).some(Boolean),
        }
    }, [enabled, found, failed, users, wanted])
}
